import { OfflineQueuePayload } from '@/db/models/OfflineQueueModel';
import { HeadTypeName } from '@/constants';
import { TourBagBsg, TourBagDetail, TourBagIronDetail, TourBagIronSet, TourBagMstrPayload } from '@/api/models';

// builds the structured payload and identifies the entity ID for the offline queue.
export const buildOfflinePayload = (
  entity: TourBagBsg | TourBagIronSet | TourBagIronDetail | TourBagDetail,
  headType: HeadTypeName | '',
  tourBag: TourBagMstrPayload,
): { payload: OfflineQueuePayload; entityId: string; entityType: HeadTypeName } | null => {
  let entityId: string;
  let entityType: HeadTypeName;

  // Clone the bag and clear all arrays to only include the edited entity
  const filteredBag: TourBagMstrPayload = {
    ...tourBag,
    tourBagDetail: [],
    tourBagBsg: [],
    tourBagIronDetail: [],
    tourBagIronSets: [],
  };

  switch (headType) {
    // BSG does not have a headTypeDescription
    case 'Ball,Shoe,Glove':
    case '': {
      const bsg = entity as TourBagBsg;
      entityType = 'Ball,Shoe,Glove' as HeadTypeName;
      filteredBag.tourBagBsg = [bsg];
      entityId = bsg.rowGuid;
      break;
    }

    case 'Iron':
    case 'Utility': {
      // distinguish between set and detail.
      const isSet = 'tourBagIronDetails' in entity && Array.isArray(entity.tourBagIronDetails);
      if (isSet) {
        const set = entity as TourBagIronSet;
        entityType = headType;
        filteredBag.tourBagIronSets = [set];
        entityId = set.rowGuid;
      } else {
        const iron = entity as TourBagIronDetail;
        entityType = headType;
        filteredBag.tourBagIronDetail = [iron];
        entityId = iron.rowGuid;
      }
      break;
    }

    default: {
      // wedge, putter, and metals (Driver, Fairway, Hybrid)
      const club = entity as TourBagDetail;
      entityType = headType;
      filteredBag.tourBagDetail = [club];
      entityId = club.rowGuid;
      break;
    }
  }

  return { payload: filteredBag, entityId, entityType };
};

// extracts the specific club or set that was modified based on clubId or rowGuid.
export const extractEntity = (tourBag: TourBagMstrPayload, clubId: number = 0) => {
  // BSG does not have a headTypeDescription
  const headType = tourBag.headTypeDescription ? tourBag.headTypeDescription : 'Ball,Shoe,Glove';

  if (headType === 'Ball,Shoe,Glove') {
    return tourBag.tourBagBsg.find((b) => b.tourBagBsgId === clubId) || null;
  }

  if (headType === 'Iron' || headType === 'Utility') {
    // check if the clubId matches a loose iron detail or a set ID
    const detail = tourBag.tourBagIronDetail.find((iron) => iron.tourBagIronDetailId === clubId);
    if (detail) return detail;

    const set = tourBag.tourBagIronSets.find((set) => set.tourBagIronSetId === clubId);
    if (set) return set;

    // grouped logic: if the clubId corresponds to an iron inside a set, return the parent set
    for (const set of tourBag.tourBagIronSets) {
      if (set.tourBagIronDetails.some((iron) => iron.tourBagIronDetailId === clubId)) {
        return set;
      }
    }

    return null;
  }

  // Metals, Wedges, Putters
  return tourBag.tourBagDetail.find((d) => d.tourBagDetailId === clubId) || null;
};

const getMaxId = <T>(items: T[], idField: keyof T): number => {
  return items.reduce((max, item) => {
    const value = item[idField];
    return Math.max(max, typeof value === 'number' ? value : 0);
  }, 0);
};

const getNextIronId = (tourBag: TourBagMstrPayload) => {
  // take into account irons that are part of a set as well as loose irons
  const looseMax = getMaxId(tourBag.tourBagIronDetail, 'tourBagIronDetailId');
  const setInnerMax = tourBag.tourBagIronSets.reduce((max, set) => {
    const inner = getMaxId(set.tourBagIronDetails, 'tourBagIronDetailId');
    return Math.max(max, inner);
  }, 0);
  return Math.max(looseMax, setInnerMax) + 1;
};

// assigns a UUID and an incremental numeric ID for newly created items.
export const generateMetadataForNewItem = (tourBag: TourBagMstrPayload, entity: TourBagBsg | TourBagIronSet | TourBagIronDetail | TourBagDetail) => {
  if (!entity.rowGuid) {
    entity.rowGuid = crypto.randomUUID();
  }

  const headType = tourBag.headTypeDescription ? (tourBag.headTypeDescription as HeadTypeName) : 'Ball,Shoe,Glove';

  switch (headType) {
    case 'Ball,Shoe,Glove': {
      const bsg = entity as TourBagBsg;
      if (!bsg.tourBagBsgId || bsg.tourBagBsgId <= 0) {
        bsg.tourBagBsgId = getMaxId(tourBag.tourBagBsg, 'tourBagBsgId') + 1;
      }
      break;
    }

    case 'Iron':
    case 'Utility': {
      const isSet = 'tourBagIronDetails' in entity && Array.isArray(entity.tourBagIronDetails);
      if (isSet) {
        const set = entity as TourBagIronSet;
        if (!set.tourBagIronSetId || set.tourBagIronSetId <= 0) {
          set.tourBagIronSetId = getMaxId(tourBag.tourBagIronSets, 'tourBagIronSetId') + 1;
        }

        // propagate metadata to children irons
        let nextId = getNextIronId(tourBag);
        set.tourBagIronDetails = set.tourBagIronDetails.map((iron) => {
          if (!iron.rowGuid) iron.rowGuid = crypto.randomUUID();
          iron.tourBagIronSetId = set.tourBagIronSetId;
          // iron.tourBagMstrId = set.tourBagMstrId; // TOOD: Is this needed?
          if (!iron.tourBagIronDetailId || iron.tourBagIronDetailId <= 0) {
            iron.tourBagIronDetailId = nextId++;
          }
          return iron;
        });
      } else {
        const iron = entity as TourBagIronDetail;
        if (!iron.tourBagIronDetailId || iron.tourBagIronDetailId <= 0) {
          iron.tourBagIronDetailId = getNextIronId(tourBag);
        }
      }
      break;
    }

    case 'Driver':
    case 'Fairway':
    case 'Hybrid':
    case 'Wedge':
    case 'Putter':
    default: {
      const club = entity as TourBagDetail;
      if (!club.tourBagDetailId || club.tourBagDetailId <= 0) {
        club.tourBagDetailId = getMaxId(tourBag.tourBagDetail, 'tourBagDetailId') + 1;
      }
    }
  }
};

const resetId = <T, K extends keyof T>(item: T, key: K): T => ({
  ...item,
  [key]: -1, // -1 is the backend requirement to flag for entities which need creation
  rowGuid: '', // set to empty string for create op
});

// remove temporary ids for new entities
export const removeTourBagInternalId = (payload: TourBagMstrPayload) => ({
  ...payload,
  tourBagDetail: payload.tourBagDetail.map((detail) => resetId(detail, 'tourBagDetailId')),
  tourBagIronDetail: payload.tourBagIronDetail.map((iron) => resetId(iron, 'tourBagIronDetailId')),
  tourBagIronSets: payload.tourBagIronSets.map((set) => ({
    ...resetId(set, 'tourBagIronSetId'),
    tourBagIronDetails: set.tourBagIronDetails.map((setIron) => resetId(setIron, 'tourBagIronDetailId')),
  })),
  tourBagBsg: payload.tourBagBsg.map((bsg) => resetId(bsg, 'tourBagBsgId')),
});
